// The fan channels on a switch panel — a "4+1", "7+1", "5+2".
//
// ⚠️ A FAN CHANNEL IS NOT A RELAY, and drawing it as one is the bug this
// replaces. Its value is a SPEED on the ladder 10 (off) then 30…100, so as a
// boolean tile a fan at speed 30 reads "on", a fan at 10 also reads "on" (any
// non-zero is truthy), and tapping it publishes `5-1` — a value that is not on
// the ladder at all. The panel's own status frame ends in these numbers, which
// is why `channelsFor` counts them and `relayCount` does not.
//
// ⚠️ IT IS A PLAIN COMMAND, NOT A TUYA DP. `<channel>-<speed>` goes through the
// same envelope as every switch, so the ESP8266 units in the field understand
// it; the Tuya DP columns in the catalogue describe the firmware's own MCU link
// and are never published from here. See core/devices/deviceCatalog.js.
//
// Shared because a fan on channel 8 behaves identically on a phone and on a
// desktop; only the surrounding panel differs.

import React from 'react';
import { Box, Typography, ButtonBase, useTheme } from '@mui/material';
import {
  FAN_OFF, FAN_SPEEDS, fanFractionOf, fanStatusLabelOf,
} from '../../core/devices/statusFormat';

/**
 * @param values      every field of the last status frame, raw
 * @param firstIndex  0-based index of the first fan channel (= relayCount)
 * @param count       how many fan channels follow it
 * @param names       optional per-channel labels
 * @param onSet       (channelIndex0Based, speed) => void
 */
export default function FanChannelRow({
  values, firstIndex, count, names, onSet, compact = false,
}) {
  const theme = useTheme();
  const c = theme.sli;
  if (!count) return null;

  const size = compact ? 34 : 40;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1.25, width: '100%', alignItems: 'center',
    }}
    >
      {Array.from({ length: count }, (_, k) => {
        const idx = firstIndex + k;
        const raw = values && values[idx];
        // ⚠️ UNKNOWN IS NOT OFF — the same rule the relay tiles follow. Until a
        // frame arrives there is no speed to show, and painting "off" would
        // claim a running fan is stopped.
        const known = raw != null && Number.isFinite(Number(raw));
        const value = known ? Number(raw) : null;
        const running = known && value > FAN_OFF;

        return (
          <Box key={idx} sx={{ width: '100%', maxWidth: 420 }}>
            <Box sx={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5,
            }}
            >
              <Typography sx={{ color: c.text, fontSize: 13, fontWeight: 600 }}>
                {(names && names[k]) || `Fan ${count > 1 ? k + 1 : ''}`.trim()}
              </Typography>
              <Typography sx={{ color: `${c.text}99`, fontSize: 12 }}>
                {known ? fanStatusLabelOf(value) : 'Waiting for the device…'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {/* Off is its own button rather than speed zero: 10 IS off on this
                  ladder, and a speed of 0 is not a value the firmware accepts. */}
              <ButtonBase
                onClick={() => onSet(idx, FAN_OFF)}
                sx={{
                  height: size,
                  px: 1.25,
                  borderRadius: 1,
                  border: `1px solid ${c.text}44`,
                  bgcolor: known && !running ? c.button : 'transparent',
                  color: c.text,
                  fontSize: 12,
                  fontWeight: 700,
                  flex: '0 0 auto',
                }}
              >
                OFF
              </ButtonBase>

              {FAN_SPEEDS.map((speed, step) => {
                const active = known && value === speed;
                return (
                  <ButtonBase
                    key={speed}
                    onClick={() => onSet(idx, speed)}
                    sx={{
                      height: size,
                      flex: 1,
                      minWidth: 0,
                      borderRadius: 1,
                      border: `1px solid ${c.text}${active ? 'cc' : '33'}`,
                      bgcolor: active ? c.button : 'transparent',
                      color: c.text,
                      fontSize: 12,
                      fontWeight: active ? 800 : 500,
                      opacity: known ? 1 : 0.55,
                    }}
                  >
                    {step + 1}
                  </ButtonBase>
                );
              })}
            </Box>

            {/* A bar rather than a dial: it has to sit under a panel of any
                width, and the number above already says the step. */}
            <Box sx={{
              mt: 0.75, height: 4, borderRadius: 2, bgcolor: `${c.text}22`, overflow: 'hidden',
            }}
            >
              <Box sx={{
                width: `${Math.round((known ? fanFractionOf(value) : 0) * 100)}%`,
                height: '100%',
                bgcolor: running ? c.button : `${c.text}55`,
                transition: 'width 160ms',
              }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
